import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createBreeding } from "../../../../api/breeding";
import {
  fetchPartnerships,
  PartnershipView,
} from "../../../../api/partnership";
import { Button } from "../../../common/Button";
import { FormSelect } from "../../../common/FormLayout";
import { Modal, ModalActions } from "../../../common/Modal";
import { sectionToIcon } from "../../../type/default";
import { SetupError } from "../../setup/SetupLayout";
import {
  BreedingFields,
  EMPTY_BREEDING_FORM,
  useBreedingForm,
} from "../BreedingFields";

/**
 * Création d'un élevage par un installateur, chez l'un de ses clients. Seuls les
 * partenariats acceptés sont proposés ; le backend vérifie le lien et partage d'office
 * l'élevage créé avec l'installateur.
 */
export const CreateClientBreedingModal = ({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (breedingId: number) => void;
}) => {
  const { t } = useTranslation();
  const breedingForm = useBreedingForm(EMPTY_BREEDING_FORM);
  const { reset } = breedingForm;
  const [clients, setClients] = useState<PartnershipView[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [idCompany, setIdCompany] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    reset(EMPTY_BREEDING_FORM);
    setIdCompany("");
    setCompanyError("");
    setError("");

    let cancelled = false;
    setLoadingClients(true);
    fetchPartnerships({ role: "provider", status: "accepted" }).then(
      (result) => {
        if (cancelled) return;
        setClients(result.partnerships);
        setError(result.error ? t("breeding-client-load-error") : "");
        setLoadingClients(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [isOpen, reset, t]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const isFormValid = breedingForm.validate();
    setCompanyError(idCompany ? "" : t("breeding-client-required"));
    if (!isFormValid || !idCompany) return;

    setSubmitting(true);
    const result = await createBreeding({
      ...breedingForm.form,
      country: "FR",
      id_company: Number(idCompany),
    });
    setSubmitting(false);

    if (!result.success || !result.breedingId) {
      setError(t("setup-error-breeding"));
      return;
    }
    onCreated(result.breedingId);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("breeding-create")}
      icon={sectionToIcon.section_breeding}
      maxWidth="md"
    >
      {loadingClients ? (
        <p className="py-4 text-tertiary">{t("loading")}</p>
      ) : clients.length === 0 && !error ? (
        <div className="py-4 space-y-4">
          <p className="text-default-text">{t("breeding-client-none")}</p>
          <ModalActions>
            <Button onClick={onClose}>{t("button-close")}</Button>
          </ModalActions>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <SetupError message={error || null} />
          <FormSelect
            label={t("breeding-client-company")}
            required
            value={idCompany}
            onChange={(e) => {
              setIdCompany(e.target.value);
              setCompanyError("");
            }}
            options={clients.map((client) => ({
              value: String(client.company.id),
              label: client.company.company_name,
            }))}
            error={companyError}
          />
          <BreedingFields breedingForm={breedingForm} />
          <ModalActions>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t("button-cancel")}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t("setup-saving") : t("breeding-create")}
            </Button>
          </ModalActions>
        </form>
      )}
    </Modal>
  );
};
